/**
 * @remarks
 * Shared campaign-related types to avoid circular dependencies between api/hub.ts and campaign modules
 */
import { REFERRAL_SUPPORTED_EXCHANGES } from "../constants/supported-cex";
import { z } from "zod";

// Campaign Type Enum
/**
 * Valid campaign types in the system
 * - TTE: Trade-to-Earn campaigns
 * - HTE: Hold-to-Earn campaigns
 * - SPINNING_WHEEL: Spinning wheel reward campaigns
 * - CASHBACK: Trading fee cashback
 * - REFERRAL: Referral reward campaigns
 * - ARENA: Cede Arena virtual-balance withdrawals (claimed from the rewards-safe like other rewards)
 */
export const CampaignTypeSchema = z.enum(["TTE", "HTE", "SPINNING_WHEEL", "CASHBACK", "REFERRAL", "ARENA"]);

export type CampaignType = z.infer<typeof CampaignTypeSchema>;

// Social Network Types
export const SOCIAL_NETWORK = {
  DISCORD: "discord",
  X: "x",
  TELEGRAM: "telegram", // Used for regular Telegram account linking for Web App users
  NATIVE_TELEGRAM: "nativeTelegram", // Used for native Telegram account linking for TMA users
} as const;

export type SocialName = (typeof SOCIAL_NETWORK)[keyof typeof SOCIAL_NETWORK];

/**
 * How much an OAuth link may do. `profile` reads the account (username,
 * avatar, creation date) and nothing else; `full` also unlocks the Hub quest
 * actions (follow, like, post) and offline access.
 */
export const SOCIAL_OAUTH_ACCESS = {
  FULL: "full",
  PROFILE: "profile",
} as const;

export type SocialOAuthAccess = (typeof SOCIAL_OAUTH_ACCESS)[keyof typeof SOCIAL_OAUTH_ACCESS];

export const SOCIAL_NETWORK_DISPLAY_NAMES = {
  [SOCIAL_NETWORK.DISCORD]: "Discord",
  [SOCIAL_NETWORK.X]: "X",
  [SOCIAL_NETWORK.TELEGRAM]: "Telegram",
  [SOCIAL_NETWORK.NATIVE_TELEGRAM]: "Telegram",
} as const;

// Trade Eligibility Types
/**
 * @remarks
 * The reasons why a user trades won't be eligible towards rewards and will be WARNED from trading
 */
export const WarnedTradeReason = {
  MAX_USER_VOLUME_BY_EPOCH_REACHED: "max_user_volume_by_epoch_reached",
  COOLDOWN_VOLUME_REACHED: "cooldown_volume_reached",
} as const;

export type WarnedTradeReason = (typeof WarnedTradeReason)[keyof typeof WarnedTradeReason];

/**
 * @remarks
 * The reasons why a user trades won't be eligible towards rewards and will be BLOCKED from trading
 */
export const BlockedTradeReason = {
  USER_BANNED: "user_banned",
  LIMIT_REACHED: "limit_reached",
  CAMPAIGN_NOT_STARTED: "campaign_not_started",
  CAMPAIGN_PAUSED: "campaign_paused",
  CAMPAIGN_PAST: "campaign_past",
  MAX_DEFI_ADDRESSES_BY_EXCHANGE_USER_ID: "max_defi_addresses_by_exchange_user_id",
  EXCHANGE_ACCOUNT_ALREADY_TRADED: "exchange_account_already_traded",
  ALREADY_TRADED: "already_traded",
  ALREADY_TRADED_ON_EXCHANGE: "already_traded_on_exchange",
  CAMPAIGN_OPT_IN_REQUIRED: "campaign_opt_in_required",
  HTE_NOT_JOINED: "hte_not_joined",
  HTE_OPTED_OUT: "hte_opted_out",
  HTE_NO_SPOTS_AVAILABLE: "hte_no_spots_available",
  HTE_NO_EXCHANGE_ACCOUNT: "hte_no_exchange_account",
  ALREADY_PARTICIPATED: "already_participated",
  MAX_TOTAL_VOLUME_BY_EPOCH_REACHED: "max_total_volume_by_epoch_reached",
  RESTRICTED_PARTICIPANTS: "restricted_participants",
  WALLET_TOO_NEW: "wallet_too_new",
  NOT_AFFILIATED_REFERRAL: "not_affiliated_referral",
  EXTERNAL_ORDER_DETECTED: "external_order_detected",
  PERPS_PNL_OPT_IN_REQUIRED: "perps_pnl_opt_in_required",
} as const;

export type BlockedTradeReason = (typeof BlockedTradeReason)[keyof typeof BlockedTradeReason];
export const blockedTradeReasons: ReadonlySet<string> = new Set(Object.values(BlockedTradeReason));

/**
 * @remarks
 * The reasons why a user trades won't be eligible towards rewards
 */
export const UneligibleTradeReason = {
  ...BlockedTradeReason,
  ...WarnedTradeReason,
} as const;

export type UneligibleTradeReason = (typeof UneligibleTradeReason)[keyof typeof UneligibleTradeReason];

// Wallet Age Eligibility Types
/**
 * Schema for wallet age eligibility check result.
 * Note: We intentionally don't expose requiredAgeMs to avoid revealing the exact rule.
 */
export const WalletAgeCheckResultSchema = z.object({
  isEligible: z.boolean(),
  checkedAt: z.number(),
  /** User-friendly reason message when wallet is ineligible, null when eligible */
  reason: z.string().nullable(),
  /** ISO date string when the wallet will become eligible, null when eligible or no transactions */
  eligibleAfter: z.string().nullable(),
});

/**
 * Result of a wallet age eligibility check.
 * Used for DEX campaign participation validation.
 */
export type WalletAgeCheckResult = z.infer<typeof WalletAgeCheckResultSchema>;

// Referral/Affiliate Exchange Types
// Note: REFERRAL_SUPPORTED_EXCHANGES is defined in constants/supported-cex.ts
export const ReferralSupportedExchangeIdSchema = z.enum(REFERRAL_SUPPORTED_EXCHANGES);

export type ReferralSupportedExchangeId = z.infer<typeof ReferralSupportedExchangeIdSchema>;
